/**
 * 計助ダッシュボード: データ入力の単一エントリ。
 *
 * 方針（詳細は `keisukeDashboardDataGridLayout.ts`）:
 * - ストアの正は常に `gridData` + `gridHeaders`（長形式互換）。
 * - 表の見た目・セル操作の基本形は `KeisukeUnifiedDataGrid.module.scss` で共通化。
 * - 入力形式ごとにレイアウト種別だけ切替え、長形式⇔専用形は `keisukeInputFormUtils` で往復。
 */

import type {
  DataRow,
  InputFormTypeKey,
  VariableMapping,
} from '../../../../stores/systems/keisuke/dashboardStore';
import KeisukeDashboardDataFormOneWayColumns from './KeisukeDashboardDataFormOneWayColumns';
import KeisukeDashboardDataFormTTestTwoColumns from './KeisukeDashboardDataFormTTestTwoColumns';
import KeisukeDashboardDataFormTwoWayBlocks from './KeisukeDashboardDataFormTwoWayBlocks';
import KeisukeDashboardDataFormDoseResponse from './KeisukeDashboardDataFormDoseResponse';
import KeisukeDashboardDataFormSurvival from './KeisukeDashboardDataFormSurvival';
import KeisukeDashboardDataFormRepeated from './KeisukeDashboardDataFormRepeated';
import KeisukeDashboardLongFormatTable from './KeisukeDashboardLongFormatTable';
import paneStyles from './KeisukeDashboardCenterPane.module.scss';
import { getKeisukeDashboardDataGridLayout } from './keisukeDashboardDataGridLayout';

export interface KeisukeDashboardUnifiedInputGridProps {
  inputFormType: InputFormTypeKey;
  gridData: DataRow[];
  gridHeaders: string[];
  disabled: boolean;
  sampleKey: string | null;
  longFormatTwoColWidthPx: number;
  onLinkedDataColumnWidthCommit: (px: number) => void;
  onLongFormatDataChange: (next: DataRow[]) => void;
  onRenameColumn: (colIndex: number, rawName: string) => boolean;
  onTTestApply: (data: DataRow[], headers: string[], opts?: { paired?: boolean }) => void;
  onOneWayApply: (data: DataRow[], headers: string[]) => void;
  onTwoWayApply: (data: DataRow[], headers: string[]) => void;
  onDoseResponseApply: (data: DataRow[], headers: string[]) => void;
  doseResponseNRows: number;
  onDoseResponseNRowsChange: (n: number) => void;
  onSurvivalApply: (data: DataRow[], headers: string[]) => void;
  survivalNRows: number;
  onSurvivalNRowsChange: (n: number) => void;
  onRepeatedApply: (data: DataRow[], headers: string[]) => void;
  repeatedNRows: number;
  onRepeatedNRowsChange: (n: number) => void;
  /** 列マッピング（専用フォームがストアの列名を解決するために使用） */
  mapping?: VariableMapping;
  testType?: 'unpaired' | 'paired';
  twoWayNPerCell?: number;
  onTwoWayNPerCellChange: (n: number) => void;
  twoWayNLevelsA: number;
  onTwoWayNLevelsAChange: (n: number) => void;
  twoWayNLevelsB: number;
  onTwoWayNLevelsBChange: (n: number) => void;
  tTestNPerGroup: number;
  onTTestNPerGroupChange: (n: number) => void;
  oneWayNPerGroup: number;
  onOneWayNPerGroupChange: (n: number) => void;
  oneWayGroupCount: number;
  onOneWayGroupCountChange: (n: number) => void;
}

export default function KeisukeDashboardUnifiedInputGrid({
  inputFormType,
  gridData,
  gridHeaders,
  disabled,
  sampleKey,
  longFormatTwoColWidthPx,
  onLinkedDataColumnWidthCommit,
  onLongFormatDataChange,
  onRenameColumn,
  onTTestApply,
  onOneWayApply,
  onTwoWayApply,
  onDoseResponseApply,
  doseResponseNRows,
  onDoseResponseNRowsChange,
  onSurvivalApply,
  survivalNRows,
  onSurvivalNRowsChange,
  onRepeatedApply,
  repeatedNRows,
  onRepeatedNRowsChange,
  mapping,
  testType,
  twoWayNPerCell,
  onTwoWayNPerCellChange,
  twoWayNLevelsA,
  onTwoWayNLevelsAChange,
  twoWayNLevelsB,
  onTwoWayNLevelsBChange,
  tTestNPerGroup,
  onTTestNPerGroupChange,
  oneWayNPerGroup,
  onOneWayNPerGroupChange,
  oneWayGroupCount,
  onOneWayGroupCountChange,
}: KeisukeDashboardUnifiedInputGridProps) {
  const reactKeyBase = sampleKey ? `sample-${sampleKey}` : 'manual';
  const layout = getKeisukeDashboardDataGridLayout(inputFormType);

  switch (layout) {
    case 'columnMatrixTwoCol':
      return (
        <KeisukeDashboardDataFormTTestTwoColumns
          key={`${reactKeyBase}-t-test`}
          gridData={gridData}
          gridHeaders={gridHeaders}
          groupColumn={mapping?.groupColumn}
          valueColumn={mapping?.valueColumn}
          valueAxisLabel={mapping?.valueAxisLabel}
          testType={testType ?? 'unpaired'}
          beforeColumn={mapping?.beforeColumn}
          afterColumn={mapping?.afterColumn}
          onApply={onTTestApply}
          disabled={disabled}
          linkedDataColumnWidthPx={longFormatTwoColWidthPx}
          onLinkedDataColumnWidthCommit={onLinkedDataColumnWidthCommit}
          nPerGroup={tTestNPerGroup}
          onNPerGroupChange={onTTestNPerGroupChange}
        />
      );
    case 'columnMatrixMulti':
      return (
        <KeisukeDashboardDataFormOneWayColumns
          key={`${reactKeyBase}-one-way`}
          gridData={gridData}
          gridHeaders={gridHeaders}
          groupColumn={mapping?.groupColumn ?? mapping?.factorAColumn}
          valueColumn={mapping?.valueColumn}
          onApply={onOneWayApply}
          disabled={disabled}
          nPerGroup={oneWayNPerGroup}
          onNPerGroupChange={onOneWayNPerGroupChange}
          groupCount={oneWayGroupCount}
          onGroupCountChange={onOneWayGroupCountChange}
          linkedDataColumnWidthPx={longFormatTwoColWidthPx}
          onLinkedDataColumnWidthCommit={onLinkedDataColumnWidthCommit}
        />
      );
    case 'twoWayFactorBlock':
      return (
        <KeisukeDashboardDataFormTwoWayBlocks
          key={`${reactKeyBase}-two-way`}
          gridData={gridData}
          gridHeaders={gridHeaders}
          factorAColumn={mapping?.factorAColumn}
          factorBColumn={mapping?.factorBColumn}
          valueColumn={mapping?.valueColumn}
          nPerCell={twoWayNPerCell}
          syncResetToken={sampleKey}
          onApply={onTwoWayApply}
          onNPerCellChange={onTwoWayNPerCellChange}
          nLevelsA={twoWayNLevelsA}
          onNLevelsAChange={onTwoWayNLevelsAChange}
          nLevelsB={twoWayNLevelsB}
          onNLevelsBChange={onTwoWayNLevelsBChange}
          disabled={disabled}
          linkedDataColumnWidthPx={longFormatTwoColWidthPx}
          onLinkedDataColumnWidthCommit={onLinkedDataColumnWidthCommit}
        />
      );
    case 'doseResponseCompact':
      return (
        <KeisukeDashboardDataFormDoseResponse
          key={`${reactKeyBase}-dose-response`}
          gridData={gridData}
          gridHeaders={gridHeaders}
          doseColumn={mapping?.doseColumn}
          effectColumn={mapping?.effectColumn}
          totalColumn={mapping?.totalColumn}
          onApply={onDoseResponseApply}
          disabled={disabled}
          nRows={doseResponseNRows}
          onNRowsChange={onDoseResponseNRowsChange}
        />
      );
    case 'survivalCompact':
      return (
        <KeisukeDashboardDataFormSurvival
          key={`${reactKeyBase}-survival`}
          gridData={gridData}
          gridHeaders={gridHeaders}
          groupColumn={mapping?.survivalGroupColumn ?? mapping?.groupColumn}
          timeColumn={mapping?.timeColumn}
          eventColumn={mapping?.eventColumn}
          onApply={onSurvivalApply}
          disabled={disabled}
          nRows={survivalNRows}
          onNRowsChange={onSurvivalNRowsChange}
        />
      );
    case 'repeatedCompact':
      return (
        <KeisukeDashboardDataFormRepeated
          key={`${reactKeyBase}-repeated`}
          gridData={gridData}
          gridHeaders={gridHeaders}
          subjectColumn={mapping?.subjectColumn}
          factorColumn={mapping?.factorAColumn ?? mapping?.groupColumn}
          valueColumn={mapping?.valueColumn}
          onApply={onRepeatedApply}
          disabled={disabled}
          nRows={repeatedNRows}
          onNRowsChange={onRepeatedNRowsChange}
        />
      );
    case 'longStackedVirtual':
      return (
        <div className={paneStyles.revoGridWrap}>
          <KeisukeDashboardLongFormatTable
            reactKey={`${reactKeyBase}-${inputFormType}`}
            gridData={gridData}
            gridHeaders={gridHeaders}
            disabled={disabled}
            longFormatTwoColWidthPx={longFormatTwoColWidthPx}
            onLinkedDataColumnWidthCommit={onLinkedDataColumnWidthCommit}
            onDataChange={onLongFormatDataChange}
            onRenameColumn={onRenameColumn}
            inputFormTypeForHelp={inputFormType}
          />
        </div>
      );
    default: {
      const _x: never = layout;
      return _x;
    }
  }
}

